import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import Headings from 'admin/components/atoms/Headings';
import { Button } from 'admin/components/atoms/Buttons';
import LabeledSelect from 'admin/components/molecules/LabeledSelect';
import Select from 'admin/components/atoms/Select';
import TextBox from 'admin/components/atoms/TextBox';
import ConfirmModal from 'admin/components/atoms/ConfirmModal';
import ApiResults from 'admin/components/molecules/ApiResults';
import { getPrivateMoneys } from 'admin/actions/private-moneys';
import {
  getUserAttributesSchema,
  createUserAttributesSchema,
  updateUserAttributesSchema,
  deleteUserAttributesSchema,
} from 'admin/actions/customers';
import InputForm from './InputForm';
import { Schema, createSchemaFromObject } from './schema';

const FormWrapper = styled.div`
  ${TextBox} {
    background: white;
    width: 400px;
  }
  ${Select} {
    width: 400px;
  }
  ${Button} {
    margin: 10px;
    min-width: 100px;
  }
`;

const DuplicationError = styled.div`
  color: red;
  font-size: 0.9rem;
`;

const ModalWrapper = styled.div``;

const CreateUserAttributeSchema = ({ user }) => {
  const defaultMoney = user.defaultMoney();
  const prefix_id_str = '#/properties/';
  const crumbs = [
    { value: 'ホーム', href: '/', isCurrentPage: false },
    { value: 'サービス管理', href: '', isCurrentPage: true },
    { value: 'ウォレット登録時アンケート', href: '', isCurrentPage: true },
  ];

  const [privateMoneys, setPrivateMoneys] = useState([]);
  const [selectedMoney, setSelectedMoney] = useState({
    label: defaultMoney.name,
    value: defaultMoney,
  });

  const [userAttributesSchema, setUserAttributesSchema] = useState([]);
  const [isAlreadyExist, setIsAlreadyExist] = useState(false);
  const [apiMessages, setApiMessages] = useState(null);
  const [optionsErrMessage, setOptionsErrMessage] = useState(null);

  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const setMoneysObj = (moneys) => {
    const ops = moneys.map(m => ({ label: m.name, value: m }));
    setPrivateMoneys(ops);
  };

  const setEachProperties = (element, key, order) => {
    const elm = element;
    const omitPrefixId = idStr => idStr.substring(prefix_id_str.length);

    elm.properties[key].order = order;

    if (elm.required.indexOf(key) !== -1) {
      elm.properties[key].required = true;
    } else {
      elm.properties[key].required = false;
    }

    elm.properties[key].id = omitPrefixId(elm.properties[key].id);

    setUserAttributesSchema(oldArray => (
      [...oldArray, createSchemaFromObject(elm.properties[key])]
    ));
  };

  const setProperties = (moneyId) => {
    getUserAttributesSchema(moneyId).then((element) => {
      const propertyOrder = element.property_order;
      propertyOrder.map((key, index) => (
        setEachProperties(element, key, index + 1)
      ));
      setIsAlreadyExist(true);
    }).catch(() => setIsAlreadyExist(false));
  };

  const onSelectMoney = (op) => {
    setSelectedMoney(op);
    setUserAttributesSchema([]);
    setApiMessages(null);
    setOptionsErrMessage(null);

    setProperties(op.value.id);
  };

  useEffect(() => {
    if (user.isServiceUser()) {
      getPrivateMoneys('per_page=2000').then((moneys) => {
        setMoneysObj(moneys.rows);
      });
    } else {
      setMoneysObj(user.issuedMoneys());
    }
    setProperties(defaultMoney.id);
  }, []);

  const existsSameValue = (arr) => {
    const s = new Set(arr);
    return s.size !== arr.length;
  };

  const openSaveModal = () => {
    setIsSaveModalOpen(true);
  };

  const closeSaveModal = () => {
    setIsSaveModalOpen(false);
  };

  const openDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const addSchema = (newItem) => {
    setUserAttributesSchema(oldArray => [...oldArray, newItem]);
  };

  const deleteSchema = (index) => {
    const newArr = [...userAttributesSchema];
    if (index !== -1) {
      newArr.splice(index, 1);
      setUserAttributesSchema(newArr);
    }
  };

  const handleAddSchema = (e) => {
    e.preventDefault();
    addSchema(new Schema());
  };

  const changeOrder = (index) => {
    const oldArr = [...userAttributesSchema];
    oldArr.splice(index, 2, oldArr[index + 1], oldArr[index]);
    const newArr = oldArr.filter(Boolean);
    setUserAttributesSchema(newArr);
  };

  const createRequired = () => (
    userAttributesSchema.filter(m => m.required).map(m => m.field_label)
  );

  const createProperties = () => {
    const properties = {};
    userAttributesSchema.forEach((m) => {
      properties[m.field_label] = {
        id: `${prefix_id_str}${m.field_label}`,
        field_label: m.field_label,
        type: m.type,
        form_type: m.form_type,
        options: m.options,
        default: m.default,
        placeholder: m.placeholder,
        prefix_label: m.prefix_label,
        regex: m.regex,
        regex_error_type: String(m.regex_error_message_en).replace(/ /g, '_'),
        regex_error_message_ja: m.regex_error_message_ja,
        regex_error_message_en: m.regex_error_message_en,
        required_reason: m.required_reason,
        number_range: m.number_range,
        date_set_value: m.date_set_value,
      };
    });
    return properties;
  };

  const createPropertyOrder = () => (
    userAttributesSchema.map(m => m.field_label)
  );

  const applyDeleteUserAttributesSchema = () => {
    deleteUserAttributesSchema(selectedMoney.value.id).then(() => {
      setApiMessages([{
        text: 'User attributes schema deleted: ユーザ情報聴取テンプレートを削除しました',
      }]);
      setUserAttributesSchema([]);
      setIsAlreadyExist(false);
    }).catch((err) => {
      setApiMessages([{
        text: `Could not delete user attributes schema: ${err.message}.`,
        isError: true,
      }]);
    });
    closeDeleteModal();
  };

  const applyCreatUserAttributesSchema = (moneyId, schema) => {
    createUserAttributesSchema(moneyId, schema).then(() => {
      setIsAlreadyExist(true);
      setApiMessages([{
        text: 'User attributes schema created: ユーザ情報聴取テンプレートを作成しました',
      }]);
    }).catch((err) => {
      setApiMessages([{
        text: `Could not create user attributes schema: ${err.message}.`,
        isError: true,
      }]);
    });
  };

  const applyUpdateUserAttributesSchema = (moneyId, schema) => {
    updateUserAttributesSchema(moneyId, schema).then(() => {
      setApiMessages([{
        text: 'User attributes schema updated: ユーザ情報聴取テンプレートを更新しました',
      }]);
    }).catch((err) => {
      setApiMessages([{
        text: `Could not update user attributes schema: ${err.message}.`,
        isError: true,
      }]);
    });
  };

  const onConfirm = () => {
    const moneyId = selectedMoney.value.id;
    const propertyOrder = createPropertyOrder();
    const properties = createProperties();

    if (existsSameValue(propertyOrder)) {
      setApiMessages([{
        text: '項目名 (英語：半角英数字)が重複している箇所があります。',
        isError: true,
      }]);
      return;
    }

    for (let i = 0; i < propertyOrder.length; i += 1) {
      const property = properties[propertyOrder[i]];
      if (property.form_type === 'radio' || property.form_type === 'checkbox') {
        const values = property.options.map(prop => prop.value);
        const labels = property.options.map(prop => prop.label);
        if (existsSameValue(values) || existsSameValue(labels)) {
          setOptionsErrMessage(`入力項目エラー：項目名「${property.field_label}」にて、 選択肢・選択肢 (英語)で重複している箇所があります。`);
          return;
        }
      }
    }

    const formData = {
      required: createRequired(),
      properties,
      property_order: propertyOrder,
    };
    const schema = { schema: formData };

    if (isAlreadyExist) {
      applyUpdateUserAttributesSchema(moneyId, schema);
      setOptionsErrMessage(null);
    } else {
      applyCreatUserAttributesSchema(moneyId, schema);
      setOptionsErrMessage(null);
    }

    closeSaveModal();
  };

  const handleSave = (e) => {
    e.preventDefault();
    openSaveModal();
  };

  const handleDelete = (e) => {
    e.preventDefault();
    openDeleteModal();
  };

  const updateSchemasAt = (index, schema) => (
    setUserAttributesSchema((oldSchmeas) => {
      const newSchemas = oldSchmeas.slice();
      newSchemas[index] = schema;
      return newSchemas;
    }));

  const getSchemaIndex = schema => userAttributesSchema.indexOf(schema);

  const moveUpSchema = (schema) => {
    const index = getSchemaIndex(schema);
    if (index - 1 >= 0) {
      changeOrder(index - 1);
    }
  };

  const moveDownSchema = (schema) => {
    const index = getSchemaIndex(schema);
    if (index + 1 < userAttributesSchema.length) {
      changeOrder(index);
    }
  };

  return (
    <FormWrapper>
      <form onSubmit={handleSave}>
        <BreadCrumb crumbs={crumbs} />
        <Headings.h5>ウォレット登録時アンケート登録・編集</Headings.h5>
        <LabeledSelect
          labelText="マネー"
          options={privateMoneys}
          value={selectedMoney}
          onChange={onSelectMoney}
          placeholder="マネーを選択してください。"
        />
        {userAttributesSchema.map((schema, index) => (
          <InputForm
            key={`input-form-${schema.id}"`}
            index={index}
            schema={schema}
            setSchema={scm => updateSchemasAt(index, scm)}
            moveUpSchema={moveUpSchema}
            moveDownSchema={moveDownSchema}
            deleteSchema={deleteSchema}
          />
        ))}
        {optionsErrMessage
         ? <DuplicationError>{optionsErrMessage}</DuplicationError>
         : null}
        <Button onClick={handleAddSchema} >項目を追加する</Button>
        {
          userAttributesSchema.length > 0
            ? (
              <div>
                <Button type="submit" >アンケートを保存する</Button>
                <Button
                  type="button"
                  onClick={handleDelete}
                  style={{ backgroundColor: 'red' }}
                >
                  アンケートを削除する
                </Button>
              </div>
            )
            : null
        }
        <ApiResults messages={apiMessages} />
      </form>
      <ConfirmModal
        isModalOpen={isSaveModalOpen}
        closeModal={closeSaveModal}
        title="保存してもよろしいですか？"
        form={(
          <ModalWrapper>
            <div style={{ textAlign: 'center', margin: 10 }}>
              <Button onClick={closeSaveModal} style={{ backgroundColor: 'gray', marginLeft: 10 }} >キャンセル</Button>
              <Button onClick={onConfirm} style={{ backgroundColor: 'red', marginLeft: 10 }} >保存</Button>
            </div>
          </ModalWrapper>
        )}
      />
      <ConfirmModal
        isModalOpen={isDeleteModalOpen}
        closeModal={closeDeleteModal}
        title="削除してもよろしいですか？"
        form={(
          <ModalWrapper>
            <div style={{ textAlign: 'center', margin: 10 }}>
              <Button onClick={closeDeleteModal} style={{ backgroundColor: 'gray', marginLeft: 10 }} >キャンセル</Button>
              <Button onClick={applyDeleteUserAttributesSchema} style={{ backgroundColor: 'red', marginLeft: 10 }} >削除</Button>
            </div>
          </ModalWrapper>
        )}
      />
    </FormWrapper>
  );
};

CreateUserAttributeSchema.propTypes = {
  user: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  user: state.user,
});

export default connect(mapStateToProps)(CreateUserAttributeSchema);
